import { createContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { encryptData, decryptData } from "@/plugins/crypto";
import { axiosAuth } from "@/plugins/axios";
import type { AuthData } from "@/types/auth.context";

interface AuthContextType {
  auth: AuthData | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permissionName: string) => boolean;
  isAuthenticated: () => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [auth, setAuth] = useState<AuthData | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("auth");
    if (stored) {
      const decrypted = decryptData(stored);
      if (decrypted) setAuth(decrypted);
    }
    setReady(true);

    const onExpired = () => {
      setAuth(null);
    };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await axiosAuth.post<AuthData>("/auth/login", {
      email,
      password,
      applicationId: import.meta.env.VITE_AUTH_APP_ID || "MIPRES",
    });
    setAuth(data);
    localStorage.setItem("auth", encryptData(data));
  };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem("auth");
  };

  const hasPermission = (permissionName: string) => {
    return (
      auth?.permissions?.some(
        (p) => p.name === permissionName && p.active === 1
      ) ?? false
    );
  };

  const isAuthenticated = () => !!auth;

  return (
    <AuthContext.Provider
      value={{ auth, ready, login, logout, hasPermission, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
};
